import React from 'react';
import { MdSettings, MdClear } from 'react-icons/md';

import { ButtonSecondary, MeatballMenu, ValidationLevel, ValidationProps } from 'ui';
import { Tag } from 'app/components/tag';
import { isDisabled } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Favourite } from 'app/area/favourite/favourite';
import { FavouriteInstance } from 'app/area/favourite/favourite-context';
import { toUpdateSavedReport } from 'app/area/shared/navigation';

import { SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';

import { createDefaultSavedReport } from '../../common/create-default-saved-report';
import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { api } from '../api';
import { dashboardLayoutTypeContext } from '../constants';

import styles from './saved-reports-favourite.scss';

interface SavedReportsFavouriteProps {
  savedReport: SavedReportDashboard;

  containerWidth: number;
  prevElementSequence: number;
  nextElementSequence: number;

  display: boolean;

  validation: SavedReportValidation<SavedReportDashboard>;
  translation: DashboardAdminTranslation;

  editSavedReportById: (savedReport: SavedReportDashboard) => void;
  editSavedReports: (savedReport: SavedReportDashboard) => void;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;

  requestCustomWidth: (id: number, value: number) => void;
}

// eslint:disable-next-line: max-func-body-length
export const SavedReportsFavourite: React.FC<SavedReportsFavouriteProps> = ({
  savedReport, containerWidth, prevElementSequence, nextElementSequence, display, validation, translation,
  editSavedReportById, editSavedReports, updateSavedReport, deleteSavedReport, requestCustomWidth
}) => {

  const onEditReport = () => {
    toUpdateSavedReport(savedReport.code?.value, true);
  };

  const onEdit = () => {
    editSavedReportById(savedReport);
  };

  const onAddReport = () => {
    editSavedReports(createDefaultSavedReport(undefined, savedReport.sequence ?? 0));
  };

  const onAddReportById = () => {
    editSavedReportById(createDefaultSavedReport(undefined, savedReport.sequence ?? 0));
  };

  const onMoveUp = () => {
    updateSavedReport(savedReport.code?.value, { ...savedReport, sequence: prevElementSequence }, validation);
  };

  const onMoveDown = () => {
    updateSavedReport(savedReport.code?.value, { ...savedReport, sequence: nextElementSequence }, validation);
  };

  const onRemove = () => {
    deleteSavedReport(savedReport.code?.value);
  };

  const containerStyle = {
    width: (containerWidth ?? 0) > 0 ?
      `${containerWidth}%` :
      '0',
    ...(!display && { display: 'none' })
  };

  const hasError = Object.keys(validation ?? {})
    .some(key => (validation[key]?.messages?.length > 0) && validation[key]?.level === ValidationLevel.error);

  const hasWarnings = Object.keys(validation ?? {})
    .some(key => (validation[key]?.messages?.length > 0) && validation[key]?.level === ValidationLevel.warning);

  const validationClass = hasError
    ? styles.dashboardCellError
    : (hasWarnings ? styles.dashboardCellWarning : styles.dashboardCell);

  return <div className={styles.dashboardWrapper} style={containerStyle}>
    <div className={validationClass}>
      <div className={styles.headerWrapper}>
        <h3>{savedReport.code?.description}</h3>
        {isDisabled(savedReport.disabled)
          ? <Tag className={styles.disabledTag}>
            {translation.disabled}
          </Tag>
          : null}
        <div className={styles.toolButton}>
          <ButtonSecondary onClick={onEdit}>
            <MdSettings size={20} title={translation.edit_connection} />
          </ButtonSecondary>
        </div>
        <div className={styles.toolButton}>
          <ButtonSecondary onClick={onRemove}>
            <MdClear size={20} title={translation.remove} />
          </ButtonSecondary>
        </div>
        <div className={styles.meatballMenu}>
          <MeatballMenu closeAfterClick={true}>
            {!!savedReport.editable && <li>
              <button className="dropdown-item"
                onClick={onEditReport}>
                {translation.edit_report}
              </button>
            </li>}
            <li>
              <button className="dropdown-item"
                onClick={onAddReport}>
                {translation.add_reports}
              </button>
            </li>
            <li>
              <button className="dropdown-item"
                onClick={onAddReportById}>
                {translation.add_report_by_id}
              </button>
            </li>
            {isDefinedIncludingZero(prevElementSequence) && <li>
              <button className="dropdown-item"
                onClick={onMoveUp}>
                {translation.move_up}
              </button>
            </li>}
            {isDefinedIncludingZero(nextElementSequence) && <li>
              <button className="dropdown-item"
                onClick={onMoveDown}>
                {translation.move_down}
              </button>
            </li>}
          </MeatballMenu>
        </div>
      </div>
      {(hasError || hasWarnings)
        ? <div className={styles.favouriteError}>
          <div className={styles.warningIcon} />
          {Object.keys(validation ?? {})
            .map(key =>
              (validation[key] as ValidationProps)?.messages
                ?.map(message => message?.message)
                ?.filter(text => text)
                ?.join(' '))
            .join(' ')
          }
        </div>
        : <div className={styles.favouriteWrapper}>
          <FavouriteInstance
            hideFilters={true}
            hideHeader={true}
            hideVisualizationOptions={true}
            reportApiOverrides={api}
            layoutTypeContextId={dashboardLayoutTypeContext}
            height={savedReport?.height}>
            <Favourite
              favouriteId={savedReport?.code?.value}
              overrideReport={savedReport?.selectedReport}
              embeddedFavourite={true}
              statisticalReportExternalProps={{
                requestCustomWidth,
                reflowHandle: containerStyle.width
              }} />
          </FavouriteInstance>
        </div>}
    </div>
  </div>;
};
